import { z } from 'zod';

import { PROFILE_SORT_KEYS, type LlmProfilesSort } from './llmProfileRows';

/**
 * The LLM profiles grid's own URL vocabulary (spec 136 FR-011, data-model.md § 3).
 *
 * In `llm-profiles` rather than in `organization-admin`, even though the route that
 * validates the schema is an admin route: this feature owns and consumes the vocabulary,
 * and holding it next door inverted the dependency into a cycle — the section reached
 * into `organization-admin` to name its own public prop type while the admin page
 * reached back here for that same type. The admin route imports the schema through this
 * feature's barrel instead, which is the direction the two features already run in.
 */

/**
 * The parsed form of a `profilesSort` param.
 *
 * The pure layer's own sort shape, imported rather than restated: `llmProfileRows.ts`
 * is the single owner of what the grid can order by, and a structurally identical
 * second declaration here is how a seventh sortable column gets added to the pure layer
 * and silently stays undroppable into the address.
 */
export type ProfilesSortView = LlmProfilesSort;

/**
 * The column orders the grid offers, as they appear in the address.
 *
 * **Derived** from `PROFILE_SORT_KEYS` rather than spelled out: a closed union of
 * `<key>.<dir>` literals, so an order the grid cannot actually apply never reaches the
 * table, but one whose membership is computed from the pure layer's key list instead of
 * being a third copy of it. Add a column there and this enum grows with it.
 */
export const PROFILES_SORTS = PROFILE_SORT_KEYS.flatMap(
  (key) => [`${key}.asc`, `${key}.desc`] as const,
);

export type ProfilesSortParam = (typeof PROFILES_SORTS)[number];

/** Name ascending — what the grid shows when the address says nothing (FR-011). */
const DEFAULT_PROFILES_SORT: ProfilesSortView = { key: 'name', dir: 'asc' };

/**
 * The LLM profiles grid's view, in the address (spec 136 data-model.md § 3): the
 * column order and the filter. In the address so an order or a filter is shareable
 * and bookmarkable (FR-011).
 *
 * Both keys are **namespaced** (`profiles…`): `/admin/harnesses` is one route
 * shared by three sections, and an unprefixed `sort` here is how this section and a
 * harnesses one end up silently reading each other's params.
 *
 * `profilesQ` is coerced from any primitive: the router parses `?profilesQ=10` as
 * the number 10, and a string-only schema would drop that filter without a word —
 * exactly as `limitsSearchSchema` does for `q`. `.catch(undefined)` on both means a
 * bad bookmark falls back to the default view instead of erroring. There is
 * deliberately no `profilesProfile` key: which sheet is open stays local state.
 */
export const profilesSearchSchema = z.object({
  profilesSort: z.enum(PROFILES_SORTS).optional().catch(undefined),
  profilesQ: z
    .union([z.string(), z.number(), z.boolean()])
    .transform(String)
    .optional()
    .catch(undefined),
});

export type ProfilesSearch = z.infer<typeof profilesSearchSchema>;

/**
 * The order the grid should apply for a given address.
 *
 * Unlike `organization-admin`'s `parseSort`, an absent param resolves to the
 * **default** (name ascending) rather than to `undefined`: the grid always has an
 * order, and spelling the default out here is what lets the table take `sort` as a
 * plain prop with no local fallback.
 */
export function parseProfilesSort(
  param: ProfilesSortParam | undefined,
): ProfilesSortView {
  if (!param) return DEFAULT_PROFILES_SORT;
  // Matched against the key list rather than split-and-cast, which is how `parseSort`
  // does it: a cast here would assert a shape the compiler cannot see, and this
  // feature writes none. `find` narrows to the pure layer's own key type on its own,
  // and a key that somehow matched nothing falls back to the default rather than to a
  // lie.
  const key = PROFILE_SORT_KEYS.find((candidate) =>
    param.startsWith(`${candidate}.`),
  );
  if (!key) return DEFAULT_PROFILES_SORT;
  return { key, dir: param.endsWith('.desc') ? 'desc' : 'asc' };
}

/**
 * The address for an order — `undefined` for the default, so name-ascending is
 * **absent** from the URL instead of written into it. A shared link then says only
 * what the reader actually changed.
 */
export function profilesSortParam(
  sort: ProfilesSortView,
): ProfilesSortParam | undefined {
  if (
    sort.key === DEFAULT_PROFILES_SORT.key &&
    sort.dir === DEFAULT_PROFILES_SORT.dir
  ) {
    return undefined;
  }
  return `${sort.key}.${sort.dir}`;
}
